/**
 * Megamenu Component
 * Handles dropdown functionality for megamenu and image menu blocks
 */

class MegamenuManager {
  constructor() {
    this.activeMenu = null;
    this.hoverTimeout = null;
    this.closeTimeout = null;
    this.focusTrapHandler = null;
    this.backdrop = document.querySelector('[data-menu-backdrop]');
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupKeyboard();
    this.setupCustomScrollbars();
    document.querySelectorAll('[data-megamenu-dropdown]:not([hidden])').forEach((dropdown) => {
      this.syncActiveMegamenuFeatureImage(dropdown);
    });
  }

  setupCustomScrollbars() {
    document.querySelectorAll('.mwave-megamenu__columns').forEach(columns => {
      const thumb = columns.closest('.mwave-megamenu__columns-outer')?.querySelector('.mwave-megamenu__scrollbar-thumb');
      if (!thumb) return;
      const update = () => this.updateScrollbarThumb(columns, thumb);
      columns.addEventListener('scroll', update);
      update();
    });
  }

  updateScrollbarThumb(columns, thumb) {
    const { scrollTop, scrollHeight, clientHeight } = columns;
    const track = columns.closest('.mwave-megamenu__columns-outer')?.querySelector('.mwave-megamenu__scrollbar');
    if (!track) return;

    if (scrollHeight <= clientHeight) {
      track.hidden = true;
      return;
    }

    track.hidden = false;
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - thumbHeight);
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.top = `${thumbTop}px`;
  }

  // Returns 'mwave' or 'digi' based on the element's own class prefix
  getPrefix(element) {
    for (const cls of element.classList) {
      if (cls.startsWith('mwave-')) return 'mwave';
      if (cls.startsWith('digi-')) return 'digi';
    }
    return 'digi';
  }

  getFocusableElements(container) {
    const focusableSelectors = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const allFocusable = Array.from(container.querySelectorAll(focusableSelectors));

    // Filter out elements that are hidden or within hidden containers
    const visible = allFocusable.filter(element => {
      // Check if element itself is visible (has dimensions and is in layout)
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;

      // Check if any parent has the hidden class
      let parent = element.parentElement;
      while (parent && parent !== container) {
        if (
          parent.classList.contains('digi-megamenu__content--hidden') ||
          parent.classList.contains('mwave-megamenu__content--hidden')
        ) {
          return false;
        }
        parent = parent.parentElement;
      }

      // Check for display:none or visibility:hidden
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }

      return true;
    });

    return visible;
  }

  setupFocusTrap(dropdown) {
    // Remove any existing trap
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
    }

    this.focusTrapHandler = (e) => {
      // Only trap Tab key
      if (e.key !== 'Tab') return;

      // Only trap if focus is currently within the dropdown
      if (!dropdown.contains(document.activeElement)) return;

      const focusableElements = this.getFocusableElements(dropdown);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(document.activeElement);

      // If shift+tab on first element, go to last
      if (e.shiftKey) {
        if (document.activeElement === firstElement || currentIndex === 0) {
          e.preventDefault();
          lastElement.focus();
        }
      }
      // If tab on last element, go to first
      else {
        if (document.activeElement === lastElement || currentIndex === focusableElements.length - 1) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
  }

  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  setupEventListeners() {
    // Handle backdrop click to close menu
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        if (this.activeMenu) {
          this.closeMenu(this.activeMenu);
        }
      });
    }

    // Handle megamenu button hover and click
    document.querySelectorAll('.digi-megamenu, .mwave-megamenu').forEach(menuWrapper => {
      const button = menuWrapper.querySelector('[data-megamenu-trigger]');

      // Hover to open
      menuWrapper.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.hoverTimeout);

        this.hoverTimeout = setTimeout(() => {
          // Close other menus immediately
          if (this.activeMenu && this.activeMenu !== menuWrapper) {
            this.closeMenu(this.activeMenu);
          }
          this.openMenu(menuWrapper);
        }, 150);
      });

      menuWrapper.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);

        this.closeTimeout = setTimeout(() => {
          this.closeMenu(menuWrapper);
        }, 200);
      });

      // Click to toggle (for mouse users)
      if (button) {
        button.addEventListener('click', (e) => {
          // Prevent default to avoid any navigation
          e.preventDefault();

          const isExpanded = button.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            this.closeMenu(menuWrapper);
          } else {
            // Close other menus
            if (this.activeMenu && this.activeMenu !== menuWrapper) {
              this.closeMenu(this.activeMenu);
            }
            this.openMenu(menuWrapper);
          }
        });
      }
    });

    // Handle megamenu dropdown hover
    document.querySelectorAll('[data-megamenu-dropdown]').forEach(dropdown => {
      // For mwave menus, hover retention is scoped to the inner container panel,
      // not the full-width dropdown overlay
      const prefix = this.getPrefix(dropdown);
      const hoverTarget = prefix === 'mwave'
        ? (dropdown.querySelector('.mwave-megamenu__container') ?? dropdown)
        : dropdown;

      hoverTarget.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.hoverTimeout);
      });

      hoverTarget.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);

        // Find the corresponding button
        const dropdownFor = dropdown.getAttribute('data-dropdown-for');
        const menuWrapper = document.querySelector(`[data-menu-id="${dropdownFor}"]`);

        this.closeTimeout = setTimeout(() => {
          if (menuWrapper) {
            this.closeMenu(menuWrapper);
          }
        }, 200);
      });

      // Build image lookup map from blocks
      const imageMap = this.buildImageMap(dropdown);

      // Handle sidebar category switching
      const sidebarItems = dropdown.querySelectorAll('.digi-megamenu__sidebar-item, .mwave-megamenu__sidebar-item');

      const switchCategory = (item) => {
        const categoryIndex = item.getAttribute('data-category-index');
        const categoryTrigger = item.getAttribute('data-category-trigger');
        const prefix = this.getPrefix(item);

        // Remove active class from all sidebar items
        sidebarItems.forEach(si => {
          si.classList.remove('digi-megamenu__sidebar-item--active');
          si.classList.remove('mwave-megamenu__sidebar-item--active');
        });
        // Add active class to current item
        item.classList.add(`${prefix}-megamenu__sidebar-item--active`);

        // Hide all content sections
        const contentSections = dropdown.querySelectorAll('[data-category-content]');
        contentSections.forEach(content => {
          const contentPrefix = this.getPrefix(content);
          content.classList.add(`${contentPrefix}-megamenu__content--hidden`);
        });

        // Show corresponding content
        const targetContent = dropdown.querySelector(`[data-category-content="${categoryIndex}"]`);
        if (targetContent) {
          const targetPrefix = this.getPrefix(targetContent);
          targetContent.classList.remove(`${targetPrefix}-megamenu__content--hidden`);

          // Update feature image based on trigger
          this.updateFeatureImage(targetContent, categoryTrigger, imageMap, item.getAttribute('data-category-url'));
        }

        // Re-setup focus trap to account for new visible elements
        if (this.activeMenu) {
          this.setupFocusTrap(dropdown);
        }

        // Sync custom scrollbar for the newly visible columns
        const newColumns = targetContent?.querySelector('.mwave-megamenu__columns');
        if (newColumns) {
          const thumb = newColumns.closest('.mwave-megamenu__columns-outer')?.querySelector('.mwave-megamenu__scrollbar-thumb');
          if (thumb) this.updateScrollbarThumb(newColumns, thumb);
        }
      };

      sidebarItems.forEach(item => {
        // Mouse hover - just switch category
        item.addEventListener('mouseenter', () => {
          switchCategory(item);
        });

        // Mouse click - switch category
        item.addEventListener('click', (e) => {
          e.preventDefault();
          switchCategory(item);
        });

        // Keyboard navigation
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            switchCategory(item);

            // Move focus to first link in the newly visible content
            const categoryIndex = item.getAttribute('data-category-index');
            const targetContent = dropdown.querySelector(`[data-category-content="${categoryIndex}"]`);

            if (targetContent) {
              // Find first focusable link in this content section
              const firstLink = targetContent.querySelector('a[href]');
              if (firstLink) {
                setTimeout(() => {
                  firstLink.focus();
                }, 50); // Small delay to ensure content is visible
              }
            }
          }
        });
      });
    });

    // Handle image menu button hover and click
    document.querySelectorAll('.digi-image-menu').forEach(menuWrapper => {
      const button = menuWrapper.querySelector('[data-image-menu-trigger]');

      // Hover to open
      menuWrapper.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.hoverTimeout);

        this.hoverTimeout = setTimeout(() => {
          // Close other menus immediately
          if (this.activeMenu && this.activeMenu !== menuWrapper) {
            this.closeMenu(this.activeMenu);
          }
          this.openMenu(menuWrapper);
        }, 150);
      });

      menuWrapper.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);

        this.closeTimeout = setTimeout(() => {
          this.closeMenu(menuWrapper);
        }, 200);
      });

      // Click to toggle (for mouse users)
      if (button) {
        button.addEventListener('click', (e) => {
          // Prevent default to avoid any navigation
          e.preventDefault();

          const isExpanded = button.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            this.closeMenu(menuWrapper);
          } else {
            // Close other menus
            if (this.activeMenu && this.activeMenu !== menuWrapper) {
              this.closeMenu(this.activeMenu);
            }
            this.openMenu(menuWrapper);
          }
        });
      }
    });

    // Handle image menu dropdown hover
    document.querySelectorAll('[data-image-menu-dropdown]').forEach(dropdown => {
      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.hoverTimeout);
      });

      dropdown.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);

        // Find the corresponding button
        const dropdownFor = dropdown.getAttribute('data-dropdown-for');
        const menuWrapper = document.querySelector(`[data-menu-id="${dropdownFor}"]`);

        this.closeTimeout = setTimeout(() => {
          if (menuWrapper) {
            this.closeMenu(menuWrapper);
          }
        }, 200);
      });
    });
  }

  setupKeyboard() {
    // Handle keyboard on menu buttons
    document.querySelectorAll('.digi-megamenu, .mwave-megamenu, .digi-image-menu').forEach(menuWrapper => {
      const button = menuWrapper.querySelector('[data-megamenu-trigger], [data-image-menu-trigger]');

      if (!button) return;

      button.addEventListener('keydown', (e) => {
        // Space or Enter to toggle
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          const isExpanded = button.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            this.closeMenu(menuWrapper);
          } else {
            // Close other menus
            if (this.activeMenu && this.activeMenu !== menuWrapper) {
              this.closeMenu(this.activeMenu);
            }
            this.openMenu(menuWrapper);

            // Move focus into the dropdown after opening via keyboard
            const menuId = menuWrapper.getAttribute('data-menu-id');
            const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);

            if (dropdown) {
              // Focus the first focusable element in the dropdown
              setTimeout(() => {
                const firstFocusable = dropdown.querySelector('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
                if (firstFocusable) {
                  firstFocusable.focus();
                }
              }, 50); // Small delay to ensure dropdown is visible
            }
          }
        }
      });
    });

    // Handle escape key on dropdowns
    document.querySelectorAll('[data-megamenu-dropdown], [data-image-menu-dropdown]').forEach(dropdown => {
      dropdown.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          const dropdownFor = dropdown.getAttribute('data-dropdown-for');
          const menuWrapper = document.querySelector(`[data-menu-id="${dropdownFor}"]`);
          const button = menuWrapper?.querySelector('[data-megamenu-trigger], [data-image-menu-trigger]');

          if (menuWrapper) {
            this.closeMenu(menuWrapper);
          }

          // Return focus to button
          if (button) {
            button.focus();
          }
        }
      });
    });

    // Global escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeMenu) {
        const button = this.activeMenu.querySelector('[data-megamenu-trigger], [data-image-menu-trigger]');
        this.closeMenu(this.activeMenu);

        // Return focus to button
        if (button) {
          button.focus();
        }
      }
    });
  }

  buildImageMap(menuWrapper) {
    const imageMap = new Map();
    const imageDataElements = menuWrapper.querySelectorAll('.digi-megamenu-image-data, .mwave-megamenu-image-data');

    imageDataElements.forEach(element => {
      const trigger = element.getAttribute('data-trigger');
      const triggerLabel = element.getAttribute('data-trigger-label');
      const imageUrl = element.getAttribute('data-image-url');
      const imageAlt = element.getAttribute('data-image-alt');
      const caption = element.getAttribute('data-caption');

      if (trigger && imageUrl) {
        imageMap.set(trigger, {
          url: imageUrl,
          alt: imageAlt || '',
          caption: caption || triggerLabel || '',
          link: element.getAttribute('data-link') || ''
        });
      }
    });

    return imageMap;
  }

  updateFeatureImage(contentElement, categoryTrigger, imageMap, fallbackUrl) {
    const featureContainer = contentElement.querySelector('[data-megamenu-feature]');

    if (!featureContainer) return;

    // Check if we have an image for this trigger
    const imageData = imageMap.get(categoryTrigger);

    if (imageData) {
      const img = featureContainer.querySelector('.digi-megamenu__feature-image, .mwave-megamenu__feature-image');
      const caption = featureContainer.querySelector('.digi-megamenu__feature-text, .mwave-megamenu__feature-text');

      img.src = imageData.url;
      img.alt = imageData.alt;

      const featureLink = featureContainer.querySelector('.digi-megamenu__feature-link');
      if (featureLink) {
        featureLink.href = imageData.link || fallbackUrl || '#';
      }

      if (caption) {
        if (imageData.caption) {
          caption.textContent = imageData.caption;
          caption.hidden = false;
        } else {
          caption.textContent = '';
          caption.hidden = true;
        }
      }

      featureContainer.hidden = false;
    } else {
      const fallback = featureContainer.getAttribute('data-fallback-image');
      if (fallback) {
        const img = featureContainer.querySelector('.digi-megamenu__feature-image, .mwave-megamenu__feature-image');
        img.src = fallback;
        img.alt = '';
        featureContainer.hidden = false;
      } else {
        featureContainer.hidden = true;
      }
    }
  }

  /**
   * Applies feature image for whichever sidebar category is active (or first item).
   * HTML defaults the first category as active, but feature image only updates on hover
   * unless this runs when the panel opens.
   */
  syncActiveMegamenuFeatureImage(dropdown) {
    if (!dropdown.hasAttribute('data-megamenu-dropdown')) return;

    const activeItem =
      dropdown.querySelector('.digi-megamenu__sidebar-item--active, .mwave-megamenu__sidebar-item--active') ||
      dropdown.querySelector('.digi-megamenu__sidebar-item, .mwave-megamenu__sidebar-item');
    if (!activeItem) return;

    const categoryIndex = activeItem.getAttribute('data-category-index');
    const categoryTrigger = activeItem.getAttribute('data-category-trigger');
    const targetContent = dropdown.querySelector(`[data-category-content="${categoryIndex}"]`);
    if (!targetContent) return;

    const imageMap = this.buildImageMap(dropdown);
    this.updateFeatureImage(targetContent, categoryTrigger, imageMap, activeItem.getAttribute('data-category-url'));
  }

  openMenu(menu) {
    const menuId = menu.getAttribute('data-menu-id');
    const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);
    const trigger = menu.querySelector('[data-megamenu-trigger], [data-image-menu-trigger]');
    const icon = menu.querySelector('.digi-megamenu__icon, .digi-image-menu__icon');

    if (!dropdown) return;

    const menuPrefix = this.getPrefix(menu);

    // Add active class
    if (menu.classList.contains('digi-megamenu') || menu.classList.contains('mwave-megamenu')) {
      menu.classList.add(`${menuPrefix}-megamenu--active`);
    } else {
      menu.classList.add(`${menuPrefix}-image-menu--active`);
    }

    // Show dropdown with animation
    dropdown.removeAttribute('hidden');
    // Force reflow to ensure hidden removal is processed before adding visible class
    dropdown.offsetHeight;
    // Use correct visible class based on dropdown type
    const dropdownPrefix = this.getPrefix(dropdown);
    const visibleClass = dropdown.hasAttribute('data-megamenu-dropdown')
      ? `${dropdownPrefix}-megamenu__dropdown--visible`
      : `${dropdownPrefix}-image-menu__dropdown--visible`;
    dropdown.classList.add(visibleClass);

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'true');

    // Rotate icon
    if (icon) {
      const iconPrefix = this.getPrefix(icon);
      if (icon.classList.contains('digi-megamenu__icon') || icon.classList.contains('mwave-megamenu__icon')) {
        icon.classList.add(`${iconPrefix}-megamenu__icon--rotated`);
      } else {
        icon.classList.add(`${iconPrefix}-image-menu__icon--rotated`);
      }
    }

    // Setup focus trap
    this.setupFocusTrap(dropdown);

    // Show backdrop positioned below the menu
    if (this.backdrop) {
      const menuSection = document.querySelector('.digi-menu, .mwave-menu');
      if (menuSection) {
        const menuBottom = menuSection.getBoundingClientRect().bottom;
        this.backdrop.style.setProperty('--menu-backdrop-top', `${menuBottom}px`);
      }
      const backdropPrefix = this.getPrefix(this.backdrop);
      this.backdrop.classList.add(`${backdropPrefix}-menu-backdrop--visible`);
    }

    this.activeMenu = menu;

    if (menu.classList.contains('digi-megamenu') || menu.classList.contains('mwave-megamenu')) {
      this.syncActiveMegamenuFeatureImage(dropdown);
    }

    if (menu.classList.contains('mwave-megamenu')) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    // Sync custom scrollbar thumb for the visible columns panel
    const activeColumns = dropdown.querySelector('.mwave-megamenu__content:not(.mwave-megamenu__content--hidden) .mwave-megamenu__columns');
    if (activeColumns) {
      const thumb = activeColumns.closest('.mwave-megamenu__columns-outer')?.querySelector('.mwave-megamenu__scrollbar-thumb');
      if (thumb) this.updateScrollbarThumb(activeColumns, thumb);
    }
  }

  closeMenu(menu) {
    const menuId = menu.getAttribute('data-menu-id');
    const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);
    const trigger = menu.querySelector('[data-megamenu-trigger], [data-image-menu-trigger]');
    const icon = menu.querySelector('.digi-megamenu__icon, .digi-image-menu__icon');

    if (!dropdown) return;

    const menuPrefix = this.getPrefix(menu);

    // Remove active class
    if (menu.classList.contains('digi-megamenu') || menu.classList.contains('mwave-megamenu')) {
      menu.classList.remove(`${menuPrefix}-megamenu--active`);
    } else {
      menu.classList.remove(`${menuPrefix}-image-menu--active`);
    }

    // Hide dropdown with animation - use correct visible class based on dropdown type
    const dropdownPrefix = this.getPrefix(dropdown);
    const visibleClass = dropdown.hasAttribute('data-megamenu-dropdown')
      ? `${dropdownPrefix}-megamenu__dropdown--visible`
      : `${dropdownPrefix}-image-menu__dropdown--visible`;
    dropdown.classList.remove(visibleClass);

    // Add hidden attribute after transition completes
    const handleTransitionEnd = () => {
      if (!dropdown.classList.contains(visibleClass)) {
        dropdown.setAttribute('hidden', '');
      }
      dropdown.removeEventListener('transitionend', handleTransitionEnd);
    };
    dropdown.addEventListener('transitionend', handleTransitionEnd);

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'false');

    // Rotate icon back
    if (icon) {
      const iconPrefix = this.getPrefix(icon);
      if (icon.classList.contains('digi-megamenu__icon') || icon.classList.contains('mwave-megamenu__icon')) {
        icon.classList.remove(`${iconPrefix}-megamenu__icon--rotated`);
      } else {
        icon.classList.remove(`${iconPrefix}-image-menu__icon--rotated`);
      }
    }

    // Remove focus trap
    this.removeFocusTrap();

    // Hide backdrop
    if (this.backdrop) {
      const backdropPrefix = this.getPrefix(this.backdrop);
      this.backdrop.classList.remove(`${backdropPrefix}-menu-backdrop--visible`);
    }

    if (menu.classList.contains('mwave-megamenu')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (this.activeMenu === menu) {
      this.activeMenu = null;
    }
  }

  closeAllMenus() {
    document.querySelectorAll('.digi-megamenu--active, .mwave-megamenu--active, .digi-image-menu--active').forEach(menu => {
      this.closeMenu(menu);
    });
  }

  /**
   * Opens the mwave megamenu for a link list and activates a category panel.
   * Used by homepage slideshow sidebar triggers.
   *
   * @param {string} menuHandle - Link list handle (matches megamenu block menu setting)
   * @param {string} categoryTrigger - Handleized category title (data-category-trigger value)
   */
  openMegamenuWithCategory(menuHandle, categoryTrigger) {
    const menuWrapper =
      (menuHandle &&
        document.querySelector(`.mwave-megamenu[data-menu-handle="${menuHandle}"]`)) ||
      document.querySelector('.mwave-megamenu');

    if (!menuWrapper) return;

    const menuId = menuWrapper.getAttribute('data-menu-id');
    const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);
    if (!dropdown) return;

    clearTimeout(this.closeTimeout);
    clearTimeout(this.hoverTimeout);

    const trigger = menuWrapper.querySelector('[data-megamenu-trigger]');
    const isOpen = trigger?.getAttribute('aria-expanded') === 'true';

    if (!isOpen) {
      if (this.activeMenu && this.activeMenu !== menuWrapper) {
        this.closeMenu(this.activeMenu);
      }
      this.openMenu(menuWrapper);
    }

    if (!categoryTrigger) return;

    const categoryItem = dropdown.querySelector(`[data-category-trigger="${categoryTrigger}"]`);
    if (categoryItem) {
      categoryItem.click();
    }
  }
}

// Initialize when DOM is ready
function initMegamenuManager() {
  window.megamenuManager = new MegamenuManager();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegamenuManager);
} else {
  initMegamenuManager();
}
